// Redeeming a team invite link (RFC-1371 §7.1).
//
// The API returns a raw token, not a URL, because it does not know the console
// origin — so the console owns both halves: `buildInviteUrl` mints
// `<origin>/join/<token>`, and this page is what that link resolves to. Without
// it an invite is a string nothing can act on.
//
// Redemption fires from a JOIN BUTTON, never on mount. Opening a link is not
// consent to join an organisation: the link may have been forwarded, pasted in
// a group chat, or opened by the wrong person on a shared machine, and the
// recipient's *active team* — which is what every subsequent project and credit
// charge is scoped to — silently changes underneath them. A click costs one
// interaction and makes the enrolment something the user did.
//
// The page is a pure derivation of (auth, flags, token, mutation state). It
// holds no effect: an effect that writes phase on mount is exactly what made
// the previous version flash "Teams are not available" in red for every
// invitee. `useTeamsEnabled()` collapses "flag snapshot has not arrived yet"
// into `false`, so the mount-time read of a gate is indistinguishable from a
// gate that is genuinely off. Anything that GUARDS on a flag — as opposed to
// merely adding UI — has to wait for `isLoading` to clear first.
//
// Every terminal state gets its own sentence. §7.1's three refusals — revoked,
// expired, exhausted — arrive as distinct codes precisely so they can be told
// apart, and "this link no longer works" leaves the recipient with nothing to
// do, where "the link has expired, ask for a new one" is actionable.
//
// `GET /teams/invites/{token}` names the team before anyone accepts, so the
// page can say "Join Acme Studio" rather than "Team invitation". That call is
// DECORATION: it is unauthenticated-safe, it does not retry, and its failure is
// ignored entirely. A preview that 404s or drifts must never stop a token that
// would redeem fine — the token is the thing that matters here.

import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Users } from 'lucide-react'

import { TeamsRequestError } from '../api/teams'
import { useAuth } from '../contexts/AuthContext'
import { useActiveTeam } from '../contexts/ActiveTeamContext'
import { useFlagsContext } from '../contexts/FlagsProvider'
import { useTeamsEnabled } from '../features/teams/useTeamsEnabled'
import { useInvitePreview, useRedeemInvite } from '../hooks/useTeams'
import { cn } from '../lib/utils'
import { trackInteraction } from '../analytics/interactionAnalytics'

type Phase =
  // Auth session or flag snapshot still resolving. Says nothing about the
  // invitation, because nothing is known about it yet.
  | 'loading'
  // Signed out. The invitation is real; the account is missing.
  | 'signin-required'
  // Flags loaded and the teams gate is genuinely off for this account.
  | 'unavailable'
  // Everything checked out — waiting on the user to press Join.
  | 'ready'
  | 'redeeming'
  | 'joined'
  | 'failed'

/** A settled result. Sticky: once joined or refused, nothing re-derives it. */
type Outcome = { phase: 'joined' | 'failed'; message: string }

export default function JoinTeamPage() {
  const { t } = useTranslation('pages')
  const navigate = useNavigate()
  const { token } = useParams<{ token: string }>()
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const teamsEnabled = useTeamsEnabled()
  const { isLoading: flagsLoading } = useFlagsContext()
  const { setActiveTeamId } = useActiveTeam()
  const redeem = useRedeemInvite()
  const preview = useInvitePreview(token)

  const [outcome, setOutcome] = useState<Outcome | null>(null)

  const hasToken = token !== undefined && token !== ''

  // Order matters. "Signed out" is checked before the teams gate because the
  // flag snapshot is fetched only for an authenticated user — a signed-out
  // visitor reads `teamsEnabled === false` forever, and telling them teams are
  // unavailable when the real answer is "sign in" sends them nowhere.
  const phase: Phase = outcome
    ? outcome.phase
    : !hasToken
      ? 'failed'
      : redeem.isPending
        ? 'redeeming'
        : authLoading
          ? 'loading'
          : !isAuthenticated
            ? 'signin-required'
            : flagsLoading
              ? 'loading'
              : !teamsEnabled
                ? 'unavailable'
                : 'ready'

  const message = outcome ? outcome.message : phaseMessage(phase, hasToken, t)

  function onJoin() {
    if (!hasToken) return
    redeem.mutate(token, {
      onSuccess: (result) => {
        trackInteraction('teams_invite_join_clicked', { team_id: result.teamId, result: 'success' })
        // Land them IN the team they just joined — that is why they clicked.
        setActiveTeamId(result.teamId)
        setOutcome({
          phase: 'joined',
          message: result.alreadyMember ? t('joinTeam.alreadyMember') : t('joinTeam.joined'),
        })
      },
      onError: (err) => {
        trackInteraction('teams_invite_join_clicked', { result: 'failure' })
        setOutcome({ phase: 'failed', message: redeemFailureMessage(err, t) })
      },
    })
  }

  const isError = phase === 'failed' || phase === 'unavailable'

  return (
    <div
      className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center gap-4 px-6 text-center"
      data-testid="join-team-page"
    >
      <Users className="h-10 w-10 text-white/50" aria-hidden="true" />
      {/* Named when the preview resolved, generic when it did not. Never a
          spinner or an error — the preview is decoration. */}
      <h1 className="text-xl font-semibold text-white" data-testid="join-team-title">
        {preview.data === undefined
          ? t('joinTeam.title')
          : t('joinTeam.titleNamed', { team: preview.data.teamName })}
      </h1>
      <p
        className={cn('text-sm leading-6', isError ? 'text-destructive' : 'text-white/70')}
        data-testid={`join-team-${phase}`}
      >
        {message}
      </p>
      {phase === 'signin-required' ? (
        <Link
          to="/login"
          state={{ returnTo: hasToken ? `/join/${token}` : '/' }}
          data-testid="join-team-signin"
          className="h-9 cursor-pointer rounded-full bg-white px-5 text-sm font-semibold leading-9 text-black no-underline transition-colors hover:bg-white/90"
        >
          {t('joinTeam.signIn')}
        </Link>
      ) : phase === 'ready' || phase === 'redeeming' ? (
        <button
          type="button"
          data-testid="join-team-accept"
          onClick={onJoin}
          disabled={phase === 'redeeming'}
          className="h-9 cursor-pointer rounded-full bg-white px-5 text-sm font-semibold text-black transition-colors hover:bg-white/90 disabled:cursor-default disabled:opacity-60"
        >
          {t('joinTeam.join')}
        </button>
      ) : (
        phase !== 'loading' && (
          <button
            type="button"
            data-testid="join-team-continue"
            onClick={() => navigate('/dashboard')}
            className="h-9 cursor-pointer rounded-full bg-white px-5 text-sm font-semibold text-black transition-colors hover:bg-white/90"
          >
            {t('joinTeam.continue')}
          </button>
        )
      )}
    </div>
  )
}

/**
 * Copy for every state the page derives rather than receives from the server.
 *
 * `loading` deliberately does not mention teams or the invitation's validity —
 * both are still unknown, and a placeholder that guesses is the bug this page
 * was rewritten to kill.
 */
function phaseMessage(phase: Phase, hasToken: boolean, t: (key: string) => string): string {
  switch (phase) {
    case 'loading':
      return t('joinTeam.checking')
    case 'signin-required':
      return t('joinTeam.signInFirst')
    case 'unavailable':
      return t('joinTeam.teamsUnavailable')
    case 'ready':
      return t('joinTeam.prompt')
    case 'redeeming':
      return t('joinTeam.redeeming')
    default:
      // Only reachable for a missing/blank token; a settled failure carries its
      // own server-derived sentence and never calls in here.
      return hasToken ? t('joinTeam.failed') : t('joinTeam.missingToken')
  }
}

/**
 * §7.1's refusals, each with its own sentence.
 *
 * `invite_not_found` covers a mistyped or truncated link as well as a deleted
 * one, so its copy must not claim the invite was revoked.
 */
function redeemFailureMessage(error: Error, t: (key: string) => string): string {
  if (!(error instanceof TeamsRequestError)) return t('joinTeam.failed')
  switch (error.code) {
    case 'invite_revoked':
      return t('joinTeam.revoked')
    case 'invite_expired':
      return t('joinTeam.expired')
    case 'invite_exhausted':
      return t('joinTeam.exhausted')
    case 'member_limit_reached':
      return t('joinTeam.teamFull')
    case 'invite_not_found':
      return t('joinTeam.notFound')
    case 'teams_disabled':
      return t('joinTeam.teamsUnavailable')
    default:
      return error.message_ ?? t('joinTeam.failed')
  }
}
